import { useEffect, useState } from "react";
import { Button, Form } from "antd";
import { Body } from "../../../components/main-body/body";
import editIcon from "../../../assets/edit.svg";
import { useTranslation } from "react-i18next";
import useNotification from "../../../components/notification/notification";
import "./functionality.css";
import ApiFunctionalityManagement from "../../../api/functionality";
import classNames from "classnames";
import {
  FormFieldName,
  FormSelect,
  FormSwitch,
} from "../../../components/form/form";
import { useDispatch } from "react-redux";
import { fetchSettings } from "../../../store/slices/settings";
import useAdminEvents from "../../../hooks/useAdminEvents";

const LOCK_PERIOD_OPTIONS = [
  { value: "endOfDay", label: "End of the day" },
  { value: "endOfWeek", label: "End of the week" },
];

const Functionality = () => {
  const { t } = useTranslation();
  const [isEdit, setIsEdit] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const { openNotification } = useNotification();
  const [settings, setSettings] = useState([]);
  const [initialList, setInitial] = useState([]);
  const [form] = Form.useForm();
  const dispatch = useDispatch();

  const cancel = () => {
    setIsEdit(false);
    setHasChanges(false);
    setSettings(initialList);

    const formValues = {};
    initialList.forEach((item) => {
      formValues[item.key] = item.value;
    });
    form.setFieldsValue(formValues);
  };

  const toggleEdit = () => {
    setIsEdit((prev) => !prev);
    setHasChanges(false);
  };

  const updateSettings = async () => {
    try {
      await ApiFunctionalityManagement.update(settings);

      openNotification({
        type: "success",
        message: <p>{t("functionality.notification.success")}</p>,
      });

      setInitial(settings);
      setIsEdit(false);
      setHasChanges(false);
      dispatch(fetchSettings());
    } catch (error) {
      console.error("Failed to update functionality settings", error);
      openNotification({
        type: "error",
        message: (
          <p>
            {t("functionality.notification.error") ||
              "Failed to update settings"}
          </p>
        ),
      });
    }
  };

  const getSettings = () => {
    ApiFunctionalityManagement.get().then((r) => {
      const nextSettings = r.data.settings || [];
      setSettings(nextSettings);
      setInitial(nextSettings);

      const formValues = {};
      nextSettings.forEach((item) => {
        formValues[item.key] = item.value;
      });
      form.setFieldsValue(formValues);
    });
  };

  const onHandleChange = (key, value) => {
    if (!hasChanges) {
      setHasChanges(true);
    }

    setSettings((prev) =>
      prev.map((item) => (item.key === key ? { ...item, value } : item))
    );

    form.setFieldValue(key, value);
  };

  useEffect(() => {
    getSettings();
  }, []);

  useAdminEvents({
    onSettingChanged: (payload) => {
      if (payload?.group === "functionality") {
        getSettings();
        dispatch(fetchSettings());
      }
    },
  });

  const isBooleanSetting = (key) => key === "sitesEnable";
  const isLockPeriodSetting = (key) => key === "defaultTaskLockPeriod";

  return (
    <Body header={{ title: t("functionality.title") }}>
      <div className="profile_form-container">
        <div className="profile_form__header">
          <span className="profile_form__header__title">
            {t("functionality.settings")}
          </span>

          <div style={{ height: 40 }}>
            {isEdit ? (
              <div className="profile-header__btns">
                <Button
                  style={{
                    height: 40,
                    background: "rgba(108, 108, 137, 0.08)",
                  }}
                  onClick={cancel}
                >
                  {t("profile.btns.cancel")}
                </Button>
                <Button
                  type="primary"
                  style={{ marginLeft: 8, height: 40 }}
                  onClick={updateSettings}
                  disabled={!hasChanges}
                >
                  {t("profile.btns.save")}
                </Button>
              </div>
            ) : (
              <img
                className="profile_form__header__edit-btn"
                src={editIcon}
                alt="edit"
                role="presentation"
                onClick={toggleEdit}
              />
            )}
          </div>
        </div>

        <div className="functionality-container" style={{ marginBottom: 32 }}>
          <Form form={form}>
            {settings.map((item) => (
              <div
                key={item.key}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 24,
                  marginBottom: 24,
                }}
              >
                <div
                  style={{
                    width: 260,
                    minWidth: 260,
                    flexShrink: 0,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <FormFieldName text={`${item.name}:`} />
                </div>

                <div
                  style={{
                    flex: 1,
                    minWidth: 0,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Form.Item
                    name={item.key}
                    initialValue={item.value}
                    style={{
                      marginBottom: 0,
                      width: "100%",
                    }}
                  >
                    {isBooleanSetting(item.key) ? (
                      <div
                        style={{
                          height: 32,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <FormSwitch
                          checked={!!item.value}
                          onChange={(checked) =>
                            onHandleChange(item.key, checked)
                          }
                          readOnly={!isEdit}
                        />
                      </div>
                    ) : isLockPeriodSetting(item.key) ? (
                      <div
                        style={{
                          height: 32,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <FormSelect
                          options={LOCK_PERIOD_OPTIONS}
                          style={{ width: 260 }}
                          placeholder="Select lock period"
                          value={item.value}
                          onChange={(value) => onHandleChange(item.key, value)}
                          readonly={!isEdit}
                          getPopupContainer={(trigger) => trigger.parentNode}
                          className={classNames({
                            "form-select--edit": isEdit,
                          })}
                        />
                      </div>
                    ) : (
                      <div
                        className={classNames("chatbot-content-item-points", {
                          "chatbot-content-item-points--edit-mode": isEdit,
                        })}
                        style={{
                          height: 32,
                          display: "flex",
                          alignItems: "center",
                          padding: "0 11px",
                          boxSizing: "border-box",
                        }}
                      >
                        {String(item.value ?? "")}
                      </div>
                    )}
                  </Form.Item>
                </div>
              </div>
            ))}
          </Form>
        </div>
      </div>
    </Body>
  );
};

export default Functionality;
